import React, { useState, useEffect } from 'react';

const MixedDomainsModal = ({ onClose }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
  }, []);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className={`md-modal relative bg-white dark:bg-gray-900/95 rounded-2xl border border-gray-200/80 dark:border-gray-700/50 shadow-2xl shadow-amber-500/10 dark:shadow-amber-500/5 w-full max-w-sm mx-4 overflow-hidden transition-all duration-500 ease-out ${show ? '' : 'opacity-0 scale-95'}`}>
        {/* Top gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500" />

        <div className="px-8 pt-8 pb-4 text-center">
          {/* Icon */}
          <div className="relative mx-auto w-16 h-16 mb-5">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 opacity-15 dark:opacity-20 blur-xl" />
            <div className="relative w-16 h-16 rounded-2xl p-[2px] bg-gradient-to-br from-amber-400 via-orange-400 to-red-400 shadow-lg shadow-amber-500/20">
              <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[14px] flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
              </div>
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1.5">
            URLs must be from the same website
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            Page extraction is designed for pages from a single website, like a product catalog or directory listing.
          </p>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-amber-400/20 to-transparent my-5" />

          {/* Examples */}
          <div className="space-y-3 text-left">
            {/* Good example */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-green-500/10 via-green-500/10 to-green-500/10 dark:from-green-500/15 dark:via-green-500/15 dark:to-green-500/15 border border-green-400/15 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">Correct</span>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Multiple pages from the same site</p>
                <p className="text-xs text-gray-400 dark:text-gray-600 mt-1 font-mono">store.com/product/1, store.com/product/2</p>
              </div>
            </div>

            {/* Bad example */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-red-500/10 via-red-500/10 to-red-500/10 dark:from-red-500/15 dark:via-red-500/15 dark:to-red-500/15 border border-red-400/15 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <span className="text-sm font-semibold text-red-600 dark:text-red-400">Won't work</span>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Pages from different websites</p>
                <p className="text-xs text-gray-400 dark:text-gray-600 mt-1 font-mono">siteA.com/page, siteB.com/page</p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-8 pb-6 pt-2 space-y-2">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white hover:from-amber-600 hover:via-orange-600 hover:to-red-600 transition-all duration-200 shadow-md shadow-amber-500/20 hover:shadow-lg hover:shadow-amber-500/30 hover:scale-[1.02] transform"
          >
            Got it
          </button>
        </div>
      </div>

      <style>{`
        .md-modal {
          animation: md-shake-in 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }
        @keyframes md-shake-in {
          0% { opacity: 0; transform: scale(0.95); }
          30% { opacity: 1; transform: scale(1); }
          40% { transform: translateX(-6px); }
          50% { transform: translateX(5px); }
          60% { transform: translateX(-4px); }
          70% { transform: translateX(3px); }
          80% { transform: translateX(-1px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default MixedDomainsModal;
